import type {
  RpcRequest,
  RpcResponse,
  EventMessage,
  InitPayload,
  BridgeMessage,
} from "@aspect/plugin-sdk";

/**
 * 插件消息源 — 表示一个已注册的 Worker 或 iframe 通道
 * 同一个插件可能同时拥有 Worker（main）和多个 iframe（toolbar / viewer 等）
 */
export type PluginSource =
  | { type: "worker"; worker: Worker; pluginId: string }
  | { type: "iframe"; iframe: HTMLIFrameElement; origin: string; pluginId: string };

/** RPC 处理函数签名，由 MessageRouter 实现并注入 */
export type RpcHandler = (
  pluginId: string,
  method: string,
  params: Record<string, unknown> | undefined
) => Promise<unknown>;

/**
 * 宿主侧消息桥接器
 *
 * 负责管理所有插件的通信通道（Worker / iframe），提供：
 * - 注册/注销插件通道
 * - 向插件发送 init payload
 * - 向单个插件或全部插件广播事件
 * - 接收插件的 RPC 请求并委托给 rpcHandler 处理
 */
export class HostBridge {
  /** pluginId → 该插件的所有消息源（一个插件可能有多个 iframe + 一个 Worker） */
  private sources = new Map<string, PluginSource[]>();
  private rpcHandler: RpcHandler | null = null;

  /** 注入 RPC 处理器（通常由 MessageRouter.route 提供） */
  setRpcHandler(handler: RpcHandler): void {
    this.rpcHandler = handler;
  }

  /** 注册 Worker 通道，并开始监听其消息 */
  registerWorker(pluginId: string, worker: Worker): void {
    const source: PluginSource = { type: "worker", worker, pluginId };
    this.addSource(pluginId, source);

    worker.onmessage = (e: MessageEvent) => {
      this.handleMessage(source, e.data);
    };
  }

  /** 注册 iframe 通道，通过 window.message 事件监听，origin 用于安全校验 */
  registerIframe(pluginId: string, iframe: HTMLIFrameElement, origin: string): void {
    const source: PluginSource = { type: "iframe", iframe, origin, pluginId };
    this.addSource(pluginId, source);

    const handler = (e: MessageEvent) => {
      if (e.source !== iframe.contentWindow) return;
      if (origin !== "*" && e.origin !== origin) return;
      this.handleMessage(source, e.data);
    };
    window.addEventListener("message", handler);

    // 附加清理函数，注销时移除事件监听
    (source as PluginSource & { _cleanup?: () => void })._cleanup = () => {
      window.removeEventListener("message", handler);
    };
  }

  /** 注销插件的所有通道，终止 Worker 并清理事件监听 */
  unregisterPlugin(pluginId: string): void {
    const sources = this.sources.get(pluginId);
    if (!sources) return;

    for (const source of sources) {
      if (source.type === "worker") {
        source.worker.terminate();
      }
      const cleanup = (source as PluginSource & { _cleanup?: () => void })._cleanup;
      if (cleanup) cleanup();
    }

    this.sources.delete(pluginId);
  }

  /** 向指定插件的所有通道发送初始化消息 */
  sendInit(pluginId: string, payload: InitPayload): void {
    this.sendToPlugin(pluginId, { type: "init", payload });
  }

  /** 向指定插件发送事件 */
  sendEvent(pluginId: string, event: string, payload?: unknown): void {
    const msg: EventMessage = { type: "event", event, payload };
    this.sendToPlugin(pluginId, msg);
  }

  /** 向所有已注册插件广播事件（如主题变更） */
  broadcastEvent(event: string, payload?: unknown): void {
    const msg: EventMessage = { type: "event", event, payload };
    for (const [, sources] of this.sources) {
      for (const source of sources) {
        this.sendToSource(source, msg);
      }
    }
  }

  private addSource(pluginId: string, source: PluginSource): void {
    if (!this.sources.has(pluginId)) {
      this.sources.set(pluginId, []);
    }
    this.sources.get(pluginId)!.push(source);
  }

  private sendToPlugin(pluginId: string, msg: BridgeMessage): void {
    const sources = this.sources.get(pluginId);
    if (!sources) return;
    for (const source of sources) {
      this.sendToSource(source, msg);
    }
  }

  /** 根据通道类型选择 postMessage 发送方式 */
  private sendToSource(source: PluginSource, msg: BridgeMessage): void {
    if (source.type === "worker") {
      source.worker.postMessage(msg);
    } else if (source.type === "iframe" && source.iframe.contentWindow) {
      source.iframe.contentWindow.postMessage(msg, source.origin);
    }
  }

  /**
   * 处理来自插件的消息
   * 目前只处理 rpc-request：将请求委托给 rpcHandler，将结果/错误回传给插件
   */
  private async handleMessage(source: PluginSource, data: unknown): Promise<void> {
    if (!data || typeof data !== "object" || !("type" in data)) return;

    const msg = data as BridgeMessage;

    if (msg.type === "rpc-request") {
      const request = msg as RpcRequest;
      if (!this.rpcHandler) return;

      try {
        const result = await this.rpcHandler(source.pluginId, request.method, request.params);
        const response: RpcResponse = { type: "rpc-response", id: request.id, result };
        this.sendToSource(source, response);
      } catch (err) {
        const response: RpcResponse = {
          type: "rpc-response",
          id: request.id,
          error: {
            code: (err as { code?: string }).code ?? "INTERNAL_ERROR",
            message: err instanceof Error ? err.message : String(err),
          },
        };
        this.sendToSource(source, response);
      }
    }
  }
}
